import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscriptions/status/${sessionId}`,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    } catch (error) {
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center px-6">
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md relative z-10"
      >
        {status === "checking" && (
          <>
            <div className="w-20 h-20 rounded-full bg-aura-emerald/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-aura-emerald animate-spin" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Processing Payment...
            </h1>
            <p className="text-aura-text-secondary">Please wait while we confirm your subscription</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-aura-emerald/10 flex items-center justify-center mx-auto mb-6 aura-glow">
              <Check className="w-10 h-10 text-aura-emerald" />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-gold/10 border border-aura-gold/20 text-aura-gold text-sm font-medium mb-4">
              <Crown className="w-4 h-4" />
              <span>AURA PRO Activated</span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Welcome to AURA PRO!
            </h1>
            <p className="text-aura-text-secondary mb-8">
              Your subscription is now active. Enjoy unlimited predictions, all 29 AI strategies, and PDF reports.
            </p>
            <Button
              className="btn-primary"
              onClick={() => navigate("/dashboard")}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {(status === "timeout" || status === "error" || status === "expired") && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">!</span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              {status === "expired" ? "Session Expired" : "Something went wrong"}
            </h1>
            <p className="text-aura-text-secondary mb-8">
              {status === "timeout" 
                ? "Payment verification timed out. Please check your email for confirmation."
                : "Please try again or contact support if the issue persists."}
            </p>
            <Button
              className="btn-primary"
              onClick={() => navigate("/subscription")}
            >
              Try Again
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
